import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import SectionTabs from '../components/SectionTabs';
import ButtonItem from '../components/ButtonItem';
import ResponsiveTable from '../components/ResponsiveTable';
import { Building2, Mic2, Disc3, Music, Package, Download } from 'lucide-react';

const TYPE_STYLE = {
  CD: 'bg-sky-500/20 text-sky-300',
  LP: 'bg-violet-500/20 text-violet-300',
  굿즈: 'bg-amber-500/20 text-amber-300',
};

function PhysicalAlbumsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initialPage = parseInt(urlParams.get('page')) || 1;

  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const dataTabs = [
    { label: '권리사', to: '/Clients', icon: Building2 },
    { label: '아티스트', to: '/Artists', icon: Mic2 },
    { label: '앨범', to: '/Albums', icon: Disc3 },
    { label: '곡', to: '/Songs', icon: Music },
    { label: '음반', to: '/PhysicalAlbums', icon: Package },
  ];

  const fetchItems = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/physical-albums', {
        params: { page, limit: 15, search: searchKeyword },
      });
      setItems(res.data.items || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount || 0);
      setCurrentPage(page);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [searchKeyword]);

  useEffect(() => { fetchItems(currentPage); }, [fetchItems]);

  const handlePageChange = (page) => {
    navigate(`/PhysicalAlbums?page=${page}`);
    fetchItems(page);
  };

  const f = (n) => Number(n || 0).toLocaleString();

  const columns = React.useMemo(() => [
    {
      Header: '순번',
      id: 'row',
      Cell: ({ row }) => totalCount - ((currentPage - 1) * 15 + row.index),
    },
    {
      Header: '커버',
      id: 'cover',
      Cell: ({ row }) => {
        const src = row.original.coverUrl || row.original.album?.image;
        return src
          ? <img src={src} alt="" className="w-10 h-10 rounded object-cover mx-auto" />
          : <div className="w-10 h-10 rounded bg-white/[0.05] mx-auto" />;
      },
    },
    {
      Header: '음반명',
      id: 'albumName',
      Cell: ({ row }) => (
        <div
          className="cursor-pointer font-bold text-sky-400 hover:underline"
          onClick={() => navigate(`/PhysicalAlbums/${row.original.id}?page=${currentPage}`)}>
          {row.original.albumName || '-'}
        </div>
      ),
    },
    {
      Header: '아티스트',
      id: 'artistName',
      Cell: ({ row }) => row.original.artistName || '-',
    },
    {
      Header: '음반코드',
      id: 'code',
      Cell: ({ row }) => (
        <span className="font-mono text-xs text-slate-400">{row.original.code || '-'}</span>
      ),
    },
    {
      Header: '분류',
      id: 'type',
      Cell: ({ row }) => (
        <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_STYLE[row.original.type] || 'bg-slate-500/20 text-slate-300'}`}>
          {row.original.type}
        </span>
      ),
    },
    {
      Header: '출고가',
      id: 'supplyPrice',
      Cell: ({ row }) => `${f(row.original.supplyPrice)}원`,
    },
    {
      Header: '재고',
      id: 'stock',
      Cell: ({ row }) => (
        <span className={`font-bold ${row.original.stock <= 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
          {f(row.original.stock)}
        </span>
      ),
    },
    {
      Header: '수수료율',
      id: 'feeRate',
      Cell: ({ row }) => `${parseInt(row.original.feeRate || 0)}%`,
    },
  ], [navigate, totalCount, currentPage]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get('/api/physical-albums/export');
      const rows = res.data;
      const data = rows.map((r, i) => ({
        순번: i + 1,
        음반명: r.albumName || '',
        음반코드: r.code || '',
        아티스트: r.artistName || '',
        분류: r.type || '',
        UPC: r.upc || '',
        바코드: r.barcode || '',
        발매일: r.releaseDate || '',
        출고가: r.supplyPrice || 0,
        재고: r.stock || 0,
        수수료율: r.feeRate ? `${parseInt(r.feeRate)}%` : '',
        기획사: r.label || '',
        반품예치금: r.returnDeposit || '',
        메모: r.memo || '',
        권리사: r.shares?.map(s => `${s.client?.name || ''}(${s.rate}%)`).join(', ') || '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [6, 30, 14, 20, 8, 14, 14, 12, 10, 8, 10, 16, 12, 20, 30].map(wch => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '음반목록');
      XLSX.writeFile(wb, '음반목록.xlsx');
    } catch (e) {
      alert('다운로드 오류: ' + e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">음반 리스트</div>
      </div>

      <SectionTabs items={dataTabs} />

      <SearchInput
        placeholder="앨범명 검색"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onSearch={() => { setSearchKeyword(searchTerm); setCurrentPage(1); }}
      />

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
          <div className="text-sm text-slate-400">데이터 불러오는 중...</div>
        </div>
      ) : (
        <>
          <ResponsiveTable
            columns={columns}
            data={items}
            visibleOnMobile={['row', 'cover', 'albumName', 'artistName']}
          />
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </>
      )}

      <div className="w-full flex justify-end items-center mt-6 gap-2 flex-wrap">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 text-sm font-semibold transition-colors border border-white/10 disabled:opacity-50">
          <Download size={14} />
          {downloading ? '처리 중...' : '목록 다운로드'}
        </button>
        <ButtonItem text="등록" color="blue" onClick={() => navigate(`/PhysicalAlbumWrite?page=${currentPage}`)} />
      </div>

    </>
  );
}

export default PhysicalAlbumsPage;
